import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "./ui";

/**
 * Small action menu ("Export ▾"). `items`: [{ key, label, icon, onSelect, hint }].
 * Trigger blocks focus-on-click (onMouseDown preventDefault) so the focus ring
 * never flashes on open — see CLAUDE.md "Focus Ring Flash Rule".
 */
export default function DropdownMenu({ label, icon, items, variant = "secondary", align = "right", disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <Button
        variant={variant}
        icon={icon}
        iconRight={ChevronDown}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
      </Button>
      {open && (
        <div
          role="menu"
          className={`absolute top-full z-50 mt-1 min-w-[200px] rounded-[4px] border border-border bg-surface p-1 shadow-lg ${align === "right" ? "right-0" : "left-0"}`}
        >
          {items.map(({ key, label: itemLabel, icon: Icon, onSelect, hint }) => (
            <button
              key={key}
              type="button"
              role="menuitem"
              className="flex w-full items-start gap-2 rounded-[3px] px-2.5 py-2 text-left text-[12.5px] text-text_primary focus:outline-none focus-visible:bg-surface_alt"
              onClick={() => {
                setOpen(false);
                onSelect();
              }}
            >
              {Icon && <Icon size={14} className="mt-0.5 flex-shrink-0 text-text_muted" aria-hidden="true" />}
              <span className="flex flex-col">
                <span className="font-medium">{itemLabel}</span>
                {hint && <span className="text-[11px] text-text_muted">{hint}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
